/*
 * CMP-021 Bottom sheet — Phase A1. No dependencies. Modal action sheet for narrow viewports (SCR-016, mobile twin of
 * the look menu SCR-013): scrim + sheet with handle, title, close button and an action list.
 *   sheet.open(trigger)  shows scrim and sheet, locks page scroll, marks the page behind inert, focuses the first
 *                        action (else the close button), traps Tab / Shift+Tab inside the sheet.
 *   sheet.close()        plays the closing state (data-state="closing", 180 ms, 0 under reduced motion), then hides
 *                        and restores focus to the trigger.
 * Dismiss: Escape, scrim click, close button, choosing an action, or (touch / pen) dragging the handle or header down past 80 px
 * (or a quick flick); a shorter drag springs back. Mouse users have the close button.
 * Events (bubbling): `nc-sheet:action` {id, trigger} (fired after the sheet has closed and the page is interactive again, so the
 * host can move focus), `nc-sheet:close` {trigger}. Actions are ignored while the sheet is closing.
 */
(function () {
  // index of the next focus stop inside the trap for Tab (shift=false) or Shift+Tab (shift=true); wraps
  function getTrapIndex(current, count, shift) {
    if (count < 1) return -1;
    if (current < 0) return shift ? count - 1 : 0;
    return (current + (shift ? -1 : 1) + count) % count;
  }

  // true when a downward drag of `dy` px lasting `ms` ms should dismiss the sheet: far enough, or a quick flick
  function shouldDismiss(dy, ms) {
    if (dy <= 0) return false;
    return dy > 80 || (dy > 24 && dy / Math.max(ms, 1) > 0.5);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { getTrapIndex: getTrapIndex, shouldDismiss: shouldDismiss };
  if (typeof customElements === 'undefined' || customElements.get('nc-sheet')) return;

  var FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

  class NcSheet extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.nc-sheet__panel');
      this.scrim = this.querySelector('.nc-sheet__scrim');
      this.trigger = null;
      this.scrim.addEventListener('click', this.close.bind(this));
      this.querySelector('.nc-sheet__close').addEventListener('click', this.close.bind(this));
      this.dialog.addEventListener('keydown', this.onKeydown.bind(this));
      this.dialog.addEventListener('click', this.onActionClick.bind(this));
      this.bindDrag();
    }

    // drag-to-dismiss from the handle / header (touch and pen only; transform only, so no layout work while dragging)
    bindDrag() {
      var self = this;
      var grips = this.dialog.querySelectorAll('.nc-sheet__handle, .nc-sheet__header');
      Array.prototype.forEach.call(grips, function (grip) {
        var startY = 0, startT = 0, dy = 0, id = null, dragging = false;
        grip.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse' || !e.isPrimary || self.getAttribute('data-state') === 'closing') return;
          id = e.pointerId; startY = e.clientY; startT = e.timeStamp; dy = 0; dragging = false;
        });
        grip.addEventListener('pointermove', function (e) {
          if (id === null || e.pointerId !== id) return;
          dy = e.clientY - startY;
          if (!dragging) {
            if (dy < 6) return;
            dragging = true;
            self.dialog.style.transition = 'none';
            try { grip.setPointerCapture(id); } catch (err) { /* pointer already gone */ }
          }
          self.dialog.style.transform = 'translateY(' + Math.max(dy, 0) + 'px)';
        });
        var end = function (e) {
          if (id === null || e.pointerId !== id) return;
          var was = dragging;
          id = null; dragging = false;
          if (!was) return;
          var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          self.dialog.style.transition = reduced ? 'none' : 'transform 180ms ease-out';
          if (e.type === 'pointerup' && shouldDismiss(dy, e.timeStamp - startT)) {
            self.dialog.style.transform = 'translateY(100%)';
            self.close();
          } else {
            self.dialog.style.transform = '';
          }
        };
        grip.addEventListener('pointerup', end);
        grip.addEventListener('pointercancel', end);
      });
    }

    get isOpen() { return !this.hidden && this.getAttribute('data-state') !== 'closing'; }

    get stops() {
      return Array.prototype.slice.call(this.dialog.querySelectorAll(FOCUSABLE));
    }

    open(trigger) {
      if (this.isOpen) return;
      clearTimeout(this.timer);
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.removeAttribute('data-state');
      this.dialog.style.transform = '';
      this.dialog.style.transition = '';
      document.documentElement.setAttribute('data-nc-scroll-lock', '');
      this.setBackgroundInert(true);
      if (this.trigger && this.trigger.getAttribute && this.trigger.getAttribute('aria-haspopup')) this.trigger.setAttribute('aria-expanded', 'true');
      var first = this.dialog.querySelector('.nc-sheet__action:not(:disabled)') || this.dialog.querySelector('.nc-sheet__close');
      first.focus();
    }

    close() {
      if (this.hidden || this.getAttribute('data-state') === 'closing') return;
      var self = this;
      var trigger = this.trigger;
      this.setAttribute('data-state', 'closing');
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var pending = this.pendingAction;
      this.pendingAction = null;
      this.timer = setTimeout(function () {
        self.hidden = true;
        self.removeAttribute('data-state');
        document.documentElement.removeAttribute('data-nc-scroll-lock');
        self.setBackgroundInert(false);
        if (trigger && trigger.getAttribute('aria-haspopup')) trigger.setAttribute('aria-expanded', 'false');
        // the chosen action is announced once the page is interactive again, so the host can move focus (e.g. to the next row)
        if (pending) self.dispatchEvent(new CustomEvent('nc-sheet:action', { bubbles: true, detail: { id: pending, trigger: trigger } }));
        // restore focus to the trigger unless the host already moved it or the trigger is gone/hidden
        var moved = document.activeElement && document.activeElement !== document.body && !self.contains(document.activeElement);
        var visible = trigger && trigger.isConnected && !trigger.closest('[hidden]') && trigger.getClientRects().length > 0;
        if (!moved && visible) trigger.focus();
        self.dispatchEvent(new CustomEvent('nc-sheet:close', { bubbles: true, detail: { trigger: trigger } }));
      }, reduced ? 0 : 180);
    }

    setBackgroundInert(on) {
      var self = this;
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === self || el.contains(self) || el.tagName === 'SCRIPT') return;
        if (on) {
          if (!el.hasAttribute('inert')) { el.setAttribute('inert', ''); el.setAttribute('data-nc-sheet-inert', ''); }
        } else if (el.hasAttribute('data-nc-sheet-inert')) {
          el.removeAttribute('inert');
          el.removeAttribute('data-nc-sheet-inert');
        }
      });
    }

    onActionClick(event) {
      var action = event.target.closest('.nc-sheet__action');
      // a second click or Enter while the sheet is closing must not emit the action again
      if (!action || action.disabled || this.getAttribute('data-state') === 'closing') return;
      this.pendingAction = action.getAttribute('data-action');
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var stops = this.stops;
      var next = getTrapIndex(stops.indexOf(document.activeElement), stops.length, event.shiftKey);
      if (next < 0) return;
      event.preventDefault();
      stops[next].focus();
    }
  }

  customElements.define('nc-sheet', NcSheet);
})();
